import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { apiFetch } from "@/utils/api";

const SOURCES = [
  { value: "parts", label: "Parts" },
  { value: "systems", label: "Systems" },
  { value: "transactions", label: "Transactions" },
  { value: "requests", label: "Shipping" },
];

export function ReportsBuilderPage() {
  const [source, setSource] = useState("parts");
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [configs, setConfigs] = useState<Record<string, unknown>[]>([]);
  const [configName, setConfigName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runReport = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch<{ rows: Record<string, unknown>[] }>(
        `/reports/builder?source=${encodeURIComponent(source)}`,
      );
      setRows(data.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to run report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    apiFetch<{ configs: Record<string, unknown>[] }>("/reports/configs")
      .then((d) => setConfigs(d.configs))
      .catch(() => {});
    runReport();
  }, []);

  const saveConfig = async () => {
    try {
      await apiFetch("/reports/configs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: configName || "Untitled", dataSource: source }),
      });
      const d = await apiFetch<{ configs: Record<string, unknown>[] }>("/reports/configs");
      setConfigs(d.configs);
      setConfigName("");
    } catch (e) {
      alert(e instanceof Error ? e.message : "Save failed");
    }
  };

  const columns =
    rows.length > 0
      ? Object.keys(rows[0]).slice(0, 8).map((key) => ({
          key,
          header: key.replace(/_/g, " "),
          render: (r: Record<string, unknown>) => String(r[key] ?? "—"),
        }))
      : [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Report Builder"
        actions={
          <Button asChild variant="secondary">
            <Link to="/reports/weekly">Weekly Report</Link>
          </Button>
        }
      />
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-4 items-end">
        <div className="space-y-1.5">
          <Label>Data Source</Label>
          <Select value={source} onChange={(e) => setSource(e.target.value)} className="min-w-48">
            {SOURCES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
        </div>
        <Button onClick={runReport} disabled={loading} className="bg-[#1e3a4f] hover:bg-[#163041]">
          {loading ? "Running…" : "Run Report"}
        </Button>
        <div className="flex gap-2 items-end">
          <div className="space-y-1.5">
            <Label>Save as</Label>
            <Input value={configName} onChange={(e) => setConfigName(e.target.value)} placeholder="Config name" />
          </div>
          <Button variant="secondary" onClick={saveConfig}>Save Config</Button>
        </div>
      </div>
      {error && <p className="text-destructive">{error}</p>}
      {configs.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Saved configs: {configs.map((c) => String(c.name)).join(", ")}
        </p>
      )}
      {columns.length > 0 && (
        <DataTable columns={columns} rows={rows} rowKey={(r) => String(r.id ?? JSON.stringify(r))} />
      )}
    </div>
  );
}
